import { useLocale } from "../../../hooks/useLocale";
import { msg } from "../../../locales/translate";
import { useEffect, useState } from "react";
import { GD } from "src/GD";
import { loadClientListPage } from "src/services/clientListLoader";
import type { ClientListPage } from "src/services/clientListData";
import type { ClientFinanceAccess, ClientListRequest } from "src/services/clientListRequest";

export const useClientList = (filters: ClientListRequest, projectId?: number, financeAccess: ClientFinanceAccess = "none") => {
	useLocale();
	const [revision, setRevision] = useState(0);
	const key = JSON.stringify([filters, projectId, revision, financeAccess]);
	const [result, setResult] = useState<{ key: string; data?: ClientListPage; error?: string }>();
	useEffect(() => {
		const handler = GD.S_CLIENTS_UPDATE.subscribe(() => setRevision(value => value + 1));
		return () => { GD.S_CLIENTS_UPDATE.unsubscribe(handler); };
	}, []);
	useEffect(() => {
		if (!projectId) return;
		const controller = new AbortController();
		const timer = setTimeout(async () => {
			try {
				const context = { expectedProjectId: projectId, signal: controller.signal };
				const data = await loadClientListPage(filters, context);
				if (!controller.signal.aborted) setResult({ key, data });
			} catch (error) {
				if (!controller.signal.aborted) setResult({ key, error: error && typeof error === "object" && "message" in error ? String(error.message) : msg("couldNotLoadClients") });
			}
		}, filters.search ? 300 : 0);
		return () => { clearTimeout(timer); controller.abort(); };
	// The serialized key includes every filter and the project; stale responses are ignored.
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [key]);
	const current = result?.key === key ? result : undefined;
	return { data: current?.data, error: current?.error, busy: !!projectId && !current, retry: () => setRevision(value => value + 1) };
};
